import { useMutation, useQuery } from "@tanstack/react-query";
import type { CreateNavBadgeInput, Sort, UpdateNavBadgeInput } from "@unbox/api";
import { handleError } from "@/api/error";
import { type Pagination, toQuery } from "@/api/pagination";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client } from "../client";

export type AdminNavBadgeSortColumn = "createdAt" | "navItemId";

export const useAdminNavBadges = (pagination: Pagination, sort: Sort<AdminNavBadgeSortColumn>) => {
  return useQuery({
    queryKey: ["admin", "nav-badges", pagination.limit, pagination.offset, sort.column, sort.direction],
    queryFn: async () => {
      const res = await client.admin["ui-interactions"]["nav-badges"].$get({
        query: {
          ...toQuery(pagination),
          ...sort,
        },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });
};

export const useCreateNavBadge = () => {
  return useMutation({
    mutationFn: async (payload: CreateNavBadgeInput) => {
      const res = await client.admin["ui-interactions"]["nav-badges"].$post({ json: payload });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "nav-badges"] });
    },
  });
};

export const useUpdateNavBadge = () => {
  return useMutation({
    mutationFn: async ({ id, ...payload }: UpdateNavBadgeInput & { id: string }) => {
      const res = await client.admin["ui-interactions"]["nav-badges"][":id"].$put({ param: { id }, json: payload });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "nav-badges"] });
    },
  });
};

export const useDeleteNavBadge = () => {
  return useMutation({
    mutationFn: async (payload: { id: string }) => {
      const res = await client.admin["ui-interactions"]["nav-badges"][":id"].$delete({ param: { id: payload.id } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "nav-badges"] });
    },
  });
};
